// Shared money helpers for the Calendar's event sheet (ART-305 v2/v3) — kept out of the component files so they stay
// fast-refresh friendly.
import type { components } from "@cntrl/api-client";
import { useSettleBox } from "../../../app/hooks/useMoney.js";

type Detail = components["schemas"]["OpsCalendarItemDetail"];
type SettleBox = components["schemas"]["OutstandingSettleBox"];
type BreakdownLine = components["schemas"]["BreakdownLine"];

export function money2(cents: number | null | undefined): string {
  return ((cents ?? 0) / 100).toLocaleString(undefined, {
    style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** "Completed by Dana Brooks · Oct 19. Expenses are closed. Reopen to add more." */
export function completedLine(box: SettleBox | undefined): string {
  const by = box?.closedByName ? ` by ${box.closedByName}` : "";
  const on = box?.closedOn ? ` · ${shortDate(box.closedOn)}` : "";
  return `Completed${by}${on}. Expenses are closed. Reopen to add more.`;
}

/** The settle box for this show's payment scope — the money view's one read. */
export function useShowSettleBox(detail: Detail, enabled: boolean) {
  return useSettleBox(detail.closeoutScopeKind ?? undefined, detail.closeoutScopeId ?? undefined, enabled);
}

/** Every expense line the net subtracts, in the breakdown's order — the merch cut excluded (the settlement carries
 *  it as its own line), linked days flattened under their own labels. */
export function expenseLines(groups: components["schemas"]["BreakdownGroup"][] | null | undefined): BreakdownLine[] {
  const out: BreakdownLine[] = [];
  for (const g of groups ?? []) {
    for (const l of g.lines ?? []) {
      if (l.kind === "merch_cut") continue;
      out.push(g.dayDate ? { ...l, label: `${dayLabel(g.dayDate, g.dayKind)} · ${l.label}` } : l);
    }
  }
  return out;
}

function dayLabel(iso: string, kind: string | null | undefined): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${kind === "travel" ? "Travel day" : "Day off"} ${d}`;
}

